// @greenlight-spec: 07-source-control-pipeline.md#required-external-checks--the-orgs-own-scanners
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: decisions/0057-org-authored-scanner-steps-isolated-job.md
import * as React from 'react';
import { Loader2, Trash2 } from 'lucide-react';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useBusy } from '@/lib/useBusy';
import type { ExternalCheck } from '@greenlight/contract';

import { SegmentedControl } from './SegmentedControl';

export function ExternalCheckRow({
  check,
  onUpdate,
  onRemove,
  onSaveSteps,
}: {
  check: ExternalCheck;
  onUpdate: (body: { enforcement?: 'block' | 'warn'; is_active?: boolean }) => Promise<void>;
  onRemove: () => Promise<void>;
  onSaveSteps?: (yaml: string) => Promise<{ ok: boolean; message?: string; line?: number }>;
}): React.JSX.Element {
  const authored = check.steps_yaml !== null;
  const [yaml, setYaml] = React.useState(check.steps_yaml ?? '');
  const [yamlError, setYamlError] = React.useState<string | null>(null);
  // One flag for the row, not per control: each write reconciles this check across
  // every repo, so a second change mid-flight would race the first.
  const [busy, track] = useBusy();
  React.useEffect(() => {
    setYaml(check.steps_yaml ?? '');
  }, [check.steps_yaml]);

  return (
    <div
      className="rounded-gl-lg border border-border bg-card"
      aria-busy={busy}
      data-testid={`external-check-row-${check.check_name}`}
    >
      <div className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[15px] font-bold tracking-tight text-foreground">
            {check.check_name}
          </span>
          {check.is_active ? (
            <StatusBadge variant="active">Active</StatusBadge>
          ) : (
            <StatusBadge variant="inactive">Off</StatusBadge>
          )}
        </div>
        {authored ? (
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            Greenlight runs these steps in every app pipeline. They cannot see Greenlight secrets or
            the evidence directory. A failure fails this check only.
          </p>
        ) : (
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            Posted by GitHub App <MonoValue>{check.github_app_id}</MonoValue>. Only that app can
            satisfy this check.
          </p>
        )}

        {authored && onSaveSteps ? (
          <div className="space-y-1.5">
            <Label htmlFor={`org-scanner-yaml-${check.check_name}`}>Steps</Label>
            <Textarea
              id={`org-scanner-yaml-${check.check_name}`}
              value={yaml}
              onChange={(e) => setYaml(e.target.value)}
              spellCheck={false}
              disabled={busy}
              className="min-h-32 font-mono text-[13px]"
            />
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              className="h-11 md:h-9"
              onClick={() => {
                void track(() => onSaveSteps(yaml)).then((result) => {
                  if (result.ok) {
                    setYamlError(null);
                    return;
                  }
                  setYamlError(
                    result.line !== undefined
                      ? `Line ${result.line}: ${result.message}`
                      : (result.message ?? null),
                  );
                });
              }}
            >
              {busy ? (
                <>
                  <Loader2 className="size-4 motion-safe:animate-spin" aria-hidden />
                  Saving…
                </>
              ) : (
                'Save steps'
              )}
            </Button>
            {yamlError !== null && (
              <p role="alert" className="text-[13px] text-gl-red-text">
                {yamlError}
              </p>
            )}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <span className="inline-flex items-center gap-2">
            <Switch
              checked={check.is_active}
              onCheckedChange={(checked) => void track(() => onUpdate({ is_active: checked }))}
              disabled={busy}
              aria-label={`${check.check_name} active`}
            />
            <span className="text-[12.5px] text-muted-foreground">
              {check.is_active ? 'On' : 'Off'}
            </span>
          </span>
          <SegmentedControl
            options={[
              { value: 'block' as const, label: 'Block' },
              { value: 'warn' as const, label: 'Warn' },
            ]}
            value={check.enforcement}
            onChange={(enforcement) => void track(() => onUpdate({ enforcement }))}
            disabled={busy}
            aria-label={`${check.check_name} enforcement`}
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 text-muted-foreground md:h-9 md:w-9"
                  disabled={busy}
                  aria-label={
                    busy ? `Updating ${check.check_name}` : `Stop requiring ${check.check_name}`
                  }
                  onClick={() => void track(onRemove)}
                >
                  {busy ? (
                    <Loader2 className="size-4 motion-safe:animate-spin" aria-hidden />
                  ) : (
                    <Trash2 className="size-4" aria-hidden />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Stop requiring {check.check_name}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
}
